import { Controller } from '@hotwired/stimulus'

/**
 * Keyboard Shortcuts Controller
 *
 * Handles global keyboard shortcuts for the application.
 *
 * Usage:
 *   Add data-controller="keyboard-shortcuts" to the body tag
 *   Add data-keyboard-shortcuts-search-selector-value="selector" to specify search field
 *
 * Shortcuts:
 *   / (or Shift+7 on Norwegian keyboards) - Focus search bar
 *   x - Open price matrix
 *   m - Open maps menu
 *   esc - Close maps menu
 *   1-5 - Navigate to first 5 contracts (on contracts index page)
 */
export default class extends Controller {
  static values = {
    searchSelector: { type: String, default: 'input[type="search"]' },
    priceMatrixLinkSelector: { type: String, default: '#price-matrix-link' },
    mapsMenuToggleSelector: { type: String, default: '#maps-menu-toggle' },
    mapsMenuSelector: { type: String, default: '#maps-menu' }
  }

  connect() {
    // Bind keyboard event listener
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)
  }

  disconnect() {
    // Clean up event listener
    document.removeEventListener('keydown', this.boundHandleKeydown)
  }

  handleKeydown(event) {
    const activeElement = document.activeElement
    const mapsMenu = document.querySelector(this.mapsMenuSelectorValue)
    const isFocusInMapsMenu = mapsMenu && mapsMenu.contains(activeElement)

    // Handle "esc" key first - Close maps menu if open (even if input is focused inside)
    // This needs to happen before input detection so it works on first press
    if (event.key === 'Escape' || event.keyCode === 27) {
      if (this.isMapsMenuOpen()) {
        this.closeMapsMenu(event)
        return
      }
      // If maps menu is not open, let other esc handlers (like modals) handle it
      // But only if we're not focused inside the maps menu
      if (!isFocusInMapsMenu) {
        return
      }
    }

    // Don't trigger shortcuts when user is typing in inputs, textareas, or contenteditable elements
    // Exception: Allow shortcuts if focus is inside maps menu (handled above for esc)
    
    // Explicitly check for TEXTAREA first - always prevent shortcuts
    if (activeElement && activeElement.tagName === 'TEXTAREA') {
      return
    }

    // Explicitly check for contenteditable elements - always prevent shortcuts
    if (activeElement && activeElement.isContentEditable) {
      return
    }

    // Handle INPUT elements - only allow shortcuts for non-text inputs (checkbox, radio, button, etc.)
    if (activeElement && activeElement.tagName === 'INPUT') {
      const inputType = activeElement.type
      // Prevent shortcuts for text-like inputs
      if (inputType === 'text' || 
          inputType === 'search' || 
          inputType === 'email' || 
          inputType === 'password' || 
          inputType === 'number' ||
          inputType === 'tel' ||
          inputType === 'url' ||
          !inputType) {
        return
      }
    }

    // Handle "/" key (works for both US and Norwegian keyboards)
    // On Norwegian keyboards, Shift+7 produces "/", so checking key === "/" covers both cases
    if (event.key === '/' ||
        (event.keyCode === 55 && event.shiftKey)) {
      this.focusSearch(event)
      return
    }

    // Handle "x" key - Navigate to price matrix
    if (event.key === 'x' || event.key === 'X') {
      this.navigateToPriceMatrix(event)
      return
    }

    // Handle "m" key - Open maps menu (only if closed)
    if (event.key === 'm' || event.key === 'M') {
      this.openMapsMenu(event)
      return
    }

    // Handle number keys 1-5 - Navigate to contracts (only on contracts index page)
    if (event.key >= '1' && event.key <= '5' && !event.shiftKey && !event.ctrlKey && !event.metaKey && !event.altKey) {
      this.navigateToContract(event, parseInt(event.key))
      return
    }
  }

  focusSearch(event) {
    event.preventDefault()
    
    // Find the search field
    const searchField = document.querySelector(this.searchSelectorValue)
    
    if (searchField) {
      searchField.focus()
      // Select any existing text so user can immediately start typing
      if (searchField.select) {
        searchField.select()
      }
    }
  }

  isMapsMenuOpen() {
    const mapsMenu = document.querySelector(this.mapsMenuSelectorValue)
    if (!mapsMenu) return false
    
    // Menu is open if it doesn't have the "hidden" class
    return !mapsMenu.classList.contains('hidden')
  }

  openMapsMenu(event) {
    // Only open if currently closed
    if (!this.isMapsMenuOpen()) {
      event.preventDefault()
      
      // Find the maps menu toggle button
      const toggleButton = document.querySelector(this.mapsMenuToggleSelectorValue)
      
      if (toggleButton) {
        // Trigger a click event on the button to activate the toggle controller
        toggleButton.click()
      }
    }
  }

  closeMapsMenu(event) {
    // Only close if currently open
    if (this.isMapsMenuOpen()) {
      event.preventDefault()

      // Find the maps menu toggle button
      const toggleButton = document.querySelector(this.mapsMenuToggleSelectorValue)

      if (toggleButton) {
        // Trigger a click event on the button to activate the toggle controller
        toggleButton.click()
      }
    }
  }

  navigateToPriceMatrix(event) {
    event.preventDefault()

    // Find the price matrix link button
    const priceMatrixLink = document.querySelector(this.priceMatrixLinkSelectorValue)

    if (priceMatrixLink) {
      // Trigger a click event on the button to navigate
      priceMatrixLink.click()
    }
  }

  navigateToContract(event, contractNumber) {
    // Only work on contracts index page
    const isContractsIndex = window.location.pathname.includes('/contracts') && 
                              !window.location.pathname.match(/\/contracts\/\d+/)
    
    if (!isContractsIndex) {
      return
    }

    event.preventDefault()
    
    // Find the contract link by ID
    const contractLink = document.querySelector(`#contract-shortcut-${contractNumber}`)
    
    if (contractLink) {
      // Navigate to the contract
      window.location.href = contractLink.href
    }
  }
}
